
import { computed, defineComponent, ref, onMounted, watch } from "vue";
import QueryBuilder from "@/components/query-builder/QueryBuilder.vue";
import SelectQueryBuilder from "@/components/query-builder/SelectQueryBuilder.vue";
import KTDatatableFilter from "@/components/kt-datatable/KTDatatableFilter.vue";
import { MenuComponent, DrawerComponent } from "@/assets/ts/components";
import * as XLSX from "xlsx/xlsx";
import utils from "@/utils";
import store from "@/store";
import $bus from "@/utils/event.js";

interface IPagination {
  page: number;
  rowsPerPage: number;
}

interface IHeaderConfiguration {
  name?: string;
  key: string;
  sortingField?: string;
  sortable?: boolean;
  type?: string;
}

interface ICustomHeaderConfiguration {
  name?: string;
  key: string;
  sortingField?: string;
  sortable?: boolean;
  type?: string;
}

interface IData {
  store: string;
  selectOData: string;
  filterOData: string;
  selectCustomOData: string;
  search: string;
  defaultSort?: string;
  defaultFilter?: string;
}

export default defineComponent({
  name: "kt-datatable",
  emit: [
    "current-change",
    "sort",
    "items-per-page-change",
    "checked-rows",
    "double-click",
    "current-row-selected",
    "showFilter",
    "aplicar-filtro",
  ],
  components: {
    QueryBuilder,
    KTDatatableFilter,
    SelectQueryBuilder,
  },
  props: {
    tableHeader: {
      type: Array as () => Array<IHeaderConfiguration>,
      required: true,
    },
    tableData: {
      type: Object as () => IData,
      required: true,
    },
    tablePagination: {
      type: Object as () => IPagination,
      required: true,
    },
    pageSizes: {
      type: Array as () => Array<string>,
      required: true,
    },
    enableItemsPerPageDropdown: { type: Boolean, default: true },
    hideOnSinglePage: { type: Boolean, default: true },
    emptyTableText: { type: String, default: "Nenhum dado encontrado" },
    loading: { type: Boolean, default: false },
    order: { type: String, default: "asc" },
    sortLabel: { type: String, default: "" },
    colors: { type: Boolean, default: false },
    maxHeight: { type: String, default: "auto" },
    currentRow: { type: Number, default: -1 },
    showFilters: { type: Boolean, default: true },
    showPagination: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const data = ref([{}]);
    const totalData = ref<number>(0);
    const oData = ref(props.tableData);
    const pagination = ref(props.tablePagination);
    const order = ref(props.order);
    const label = ref(props.sortLabel);
    const currentSort = ref<string>("");
    const hidePage = ref(props.hideOnSinglePage);
    const rowsPage = ref<string>("");
    const isloading = ref(props.loading);
    const ischecked = ref(false);
    const rowsIdList = ref<number[]>([]);
    const isColors = ref(props.colors);
    const currentRowSelected = ref(props.currentRow);
    var timeOut;
    const showFilter = ref(false);
    const showTableCustom = ref(false);
    const showFilters = ref(props.showFilters);
    const showPagination = ref(props.showPagination);

    onMounted(() => {
      showFilter.value = false;
      showTableCustom.value = false;
      currentSort.value = label.value + " " + order.value;
      rowsPage.value = pagination.value.rowsPerPage.toString();
      isloading.value = true;
      loadData();
    });

    watch(pagination.value, () => {
      isloading.value = true;
      currentRowSelected.value = 0;
      emit("current-row-selected", currentRowSelected.value);
      loadData();
    });

    watch(oData.value, () => {
      isloading.value = true;
      pagination.value.page = 1;
      currentRowSelected.value = 0;
      emit("current-row-selected", currentRowSelected.value);
      loadData();
    });

    watch(currentRowSelected, () => {
      let qtdLinhas = data.value.length;
      if (currentRowSelected.value > qtdLinhas - 1) {
        currentRowSelected.value = qtdLinhas - 1;
        emit("current-row-selected", currentRowSelected.value);
      }
    });

    function loadData() {
      clearTimeout(timeOut);
      timeOut = setTimeout(loadOData, 500);
    }

    async function loadOData() {
      //console.log("entrou Odata");
      isloading.value = true;
      var page = (pagination.value.page - 1) * pagination.value.rowsPerPage;
      await store
        .dispatch(
          oData.value.store,
          (oData.value.selectCustomOData
            ? oData.value.selectCustomOData
            : oData.value.selectOData) +
            getFiltroOData() +
            "&$count=true&$skip=" +
            page +
            "&$top=" +
            pagination.value.rowsPerPage +
            (currentSort.value !== " asc" && currentSort.value !== " desc"
              ? "&$orderby=" + currentSort.value
              : oData.value.defaultSort
              ? "&$orderby=" + oData.value.defaultSort
              : "")
        )
        .then(
          (ret) => {
            if (ret.value !== null) {
              totalData.value = ret["@odata.count"];
              data.value.splice(0, data.value.length, ...ret.value);
              if (isColors.value === true) {
                for (var i = 0; i < data.value.length; i++) {
                  Object.assign(data.value[i], {
                    color: utils.getColorRandom(),
                  });
                }
              }
              isloading.value = false;
              onCheckedRows(false, null);
              setTimeout(MenuComponent.reinitialization, 500);
              setTimeout(DrawerComponent.reinitialization, 500);
              // var elemento = document.getElementById("kt_custom_table");
              // console.log(elemento);
              //console.log(JSON.stringify(data.value));
            } else {
              data.value = [];
              isloading.value = false;
              onCheckedRows(false, null);
            }
          },
          (error) => {
            data.value = [];
            isloading.value = false;
            onCheckedRows(false, null);
          }
        );
    }

    function getFiltroOData() {
      if (oData.value.defaultFilter) {
        if (oData.value.filterOData) {
          return `&$filter=(${oData.value.filterOData.replace(
            "&$filter=",
            ""
          )}) and ${oData.value.defaultFilter}`;
        } else {
          return `&$filter=(${oData.value.defaultFilter})`;
        }
      } else {
        return oData.value.filterOData;
      }
    }

    const getItems = computed(() => {
      return data.value;
    });

    const currentPageChange = (val) => {
      emit("current-change", val);
      pagination.value.page = val;
    };

    const setItemsPerPage = (event) => {
      if (utils.isNumber(event.target.value)) {
        emit("items-per-page-change", parseInt(event.target.value));
        pagination.value.rowsPerPage = parseInt(event.target.value);
      } else {
        emit("items-per-page-change", event.target.value);
        pagination.value.rowsPerPage = totalData.value;
        rowsPage.value = event.target.value;
      }
      pagination.value.page = 1;
    };

    const sort = (columnName, sortable) => {
      if (sortable === false) {
        return;
      }

      if (order.value === "asc") {
        order.value = "desc";
        emit("sort", { columnName: columnName, order: "desc" });
      } else {
        order.value = "asc";
        emit("sort", { columnName: columnName, order: "asc" });
      }

      currentSort.value = columnName + " " + order.value;
      isloading.value = true;
      loadData();
    };

    function onCheckedRows(checked, cellKey) {
      rowsIdList.value = [];
      if (checked == true) {
        for (const key of data.value) {
          rowsIdList.value.push(key[cellKey ? cellKey : "id"]);
        }
      } else {
        ischecked.value = false;
      }
      emit("checked-rows", rowsIdList.value);
    }

    function onDoubleClick(item) {
      emit("double-click", item);
    }

    const getCurrentRow = computed(() => {
      return currentRowSelected.value;
    });

    function selectCurrentRow(index) {
      currentRowSelected.value = index;
      emit("current-row-selected", index);
    }

    return {
      getItems,
      currentPageChange,
      setItemsPerPage,
      sort,
      totalData,
      pagination,
      currentSort,
      hidePage,
      rowsPage,
      isloading,
      ischecked,
      onCheckedRows,
      onDoubleClick,
      loadData,
      timeOut,
      selectCurrentRow,
      getCurrentRow,
      currentRowSelected,
      oData,
      showFilter,
      showTableCustom,
      utils,
    };
  },
  methods: {
    verificaTipoPacote() {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        if (
          user &&
          user.User &&
          user.User.Emitente &&
          user.User.Emitente.tipo_pacote === 0
        ) {
          return false;
        }
      }
      return true;
    },
    retornaSelectTablePrincipal(select) {
      this.customTableHeader.splice(0, this.customTableHeader.length);
      let selectFields: string[] = [];

      select.forEach((filtro) => {
        if (!(filtro.tipo === "object" && filtro.select)) {
          if (filtro.campo !== "" && filtro.tipo !== "") {
            const headerExistente = this.tableHeader.find(
              (header) => header.key === filtro.campo
            );

            if (!headerExistente) {
              selectFields.push(filtro.campo);

              this.customTableHeader.push({
                name:
                  this.model[filtro.campo] && this.model[filtro.campo].descricao
                    ? this.model[filtro.campo].descricao
                    : filtro.campo,
                key: filtro.campo,
                type: filtro.tipo,
                sortable: true,
              });
            }
          }
        }
      });

      let selectOdataQuery = "";

      if (selectFields.length > 0) {
        var selectOData = this.oData.selectOData.replace("?$select=", "");
        selectOdataQuery = `?$select=${selectFields.join(",")},${selectOData}`;
      }

      return selectOdataQuery;
    },
    retornaSelectTableCustom(select) {
      this.customTableHeader.splice(0, this.customTableHeader.length);
      let expandGroups = {};
      let selectFields: string[] = [];

      select.forEach((filtro) => {
        if (filtro.tipo === "object" && filtro.select) {
          this.retornaSelectOData([filtro], expandGroups);
        } else if (filtro.campo !== "" && filtro.tipo !== "") {
          selectFields.push(filtro.campo);
        }
      });

      const selectExpand = this.retornaSelectExpand(expandGroups);

      let selectOdataQuery = "";

      if (selectFields.length > 0) {
        selectOdataQuery += `?$select=${selectFields.join(",")}`;
      }

      if (selectExpand) {
        if (selectFields.length > 0) {
          selectOdataQuery += `&$expand=${selectExpand}`;
        } else {
          selectOdataQuery += `?$expand=${selectExpand}`;
        }
      }

      return selectOdataQuery;
    },
    retornaSelectOData(select, expandGroups) {
      select.forEach((filtro) => {
        if (filtro.tipo === "object" && filtro.select) {
          const campo = filtro.campo;
          if (!expandGroups[campo]) {
            expandGroups[campo] = {
              selectFields: [],
              subGroups: {},
            };
          }
          if (filtro.select.tipo === "object") {
            this.retornaSelectOData(
              [filtro.select],
              expandGroups[campo].subGroups
            );
          } else if (filtro.select.campo !== "" && filtro.select.tipo !== "") {
            const selectField = filtro.select.campo;
            expandGroups[campo].selectFields.push(selectField);
          }
        }
      });
    },
    retornaSelectExpand(expandGroups) {
      const expandQuery: string[] = [];

      for (const campo in expandGroups) {
        const selectFields = expandGroups[campo].selectFields.join(",");
        const subExpands = this.retornaSelectExpand(
          expandGroups[campo].subGroups
        );

        let expandField = campo;

        if (selectFields) {
          expandField += `($select=${selectFields}`;
        }

        if (subExpands) {
          if (selectFields) {
            expandField += ";";
          } else {
            expandField += "(";
          }
          expandField += `$expand=${subExpands}`;
        }

        if (selectFields || subExpands) {
          expandField += ")";
        }

        expandQuery.push(expandField);
      }

      return expandQuery.join(",");
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async atualizaSelectCustom() {
      if (this.showTableCustom) {
        this.oData.selectCustomOData = this.retornaSelectTableCustom(
          this.selectFiltro
        );
      } else {
        this.oData.selectCustomOData = this.retornaSelectTablePrincipal(
          this.selectFiltro
        );
      }

      this.selectQuery = this.oData.selectCustomOData;

      if (this.showFilter) {
        await this.sleep(500);
        $bus.trigger("aplicar-select-filter");
      }
    },
    confirmarCampo() {
      this.atualizaSelectCustom();
    },
    adicionarCampo() {
      this.selectFiltro.push({
        campo: "",
        tipo: "",
      });
    },
    removerCampo(index) {
      this.selectFiltro.splice(index, 1);
      if (this.selectFiltro.length === 0) {
        this.adicionarCampo();
      }
    },
    load() {
      this.loadData();
    },
    setRowsPage(value) {
      this.rowsPage = value.toString();
    },
    setLoading(value) {
      this.isloading = value;
    },
    setCurrentRow(value) {
      this.currentRowSelected = value;
    },
    getCurrentItemIndex(index) {
      return this.getItems[index];
    },
    exportarExcel() {
      const dados = XLSX.utils.json_to_sheet(this.getItems);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, dados, "Dados");
      XLSX.writeFile(wb, "export-excel.xls");
    },
    async atualizarSelectFiltro(select) {
      this.selectFiltro.splice(0, this.selectFiltro.length);
      this.selectFiltro = select;
      this.atualizaSelectCustom();
    },
    queryFilter(filtro, expandFiltro) {
      this.oData.filterOData = filtro;
      if (expandFiltro) {
        this.oData.selectCustomOData = expandFiltro;
      } else {
        this.atualizaSelectCustom();
      }
      //console.log(filtro);
      //console.log(expandFiltro);

      this.$emit("aplicar-filtro", filtro, expandFiltro);
    },
    ocultarQueryFilter() {
      this.oData.filterOData = "";
      this.atualizaSelectCustom();
      this.showFilter = false;
    },
    exibirQueryFilter() {
      this.showFilter = true;
    },
    ocultarTableCustom() {
      this.showTableCustom = false;
    },
    exibirTableCustom() {
      this.showTableCustom = true;
    },
    async getSchema() {
      var tabela = this.tableData.store
        .split("/")[1]
        .replace("get", "")
        .replace("OData", "");
      this.viewModel = tabela + "ViewModel";
      if (this.viewModel) {
        try {
          var ret = await store.dispatch("schema/getSchema", this.viewModel);
          if (ret.Success === true) {
            this.model = JSON.parse(ret.Data);
            //console.log(JSON.stringify(this.model));
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  },
  watch: {
    showFilter: {
      handler() {
        this.$emit("showFilter", this.showFilter);
      },
    },
    showTableCustom: {
      handler() {
        this.atualizaSelectCustom();
      },
    },
  },
  async mounted() {
    await this.getSchema();
  },
  data() {
    return {
      viewModel: "",
      model: {},
      selectQuery: "",
      selectFiltro: [
        {
          campo: "",
          tipo: "",
        },
      ],
      customTableHeader: [] as ICustomHeaderConfiguration[],
    };
  },
});
